/**
 * Admin System Page
 * 
 * PURPOSE: Simulation & Stress Testing ONLY
 * 
 * High-volume simulations for validating system behavior.
 * Results are diagnostic only - simulations do NOT create persistent leads.
 * 
 * Emphasizes:
 * - Health score
 * - Contract breaches  
 * - Pool starvation
 * - Retry pressure
 * 
 * This page is NOT used for production verification.
 */

import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity,
  FlaskConical,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  TrendingUp,
  Users,
  RefreshCw,
  Package,
  Zap,
  ShieldCheck,
  PlugZap,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  runPresetSimulation,
  formatResultSummary,
  ALL_PRESETS,
  type SimulationPresetName,
  type SimulationResult,
} from "@/lib/simulation";
import { routeNewLeads, type RouteNewLeadsResult } from "@/lib/ops/routeNewLeads";
import { DevAgentControlsCard } from "@/components/admin/DevAgentControlsCard";
import { SystemHealthDiagnostics } from "@/components/admin/SystemHealthDiagnostics";
import { isDevMode } from "@/lib/featureFlags";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export default function AdminSystem() {
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null);
  const [activePreset, setActivePreset] = useState<SimulationPresetName | null>(null);
  const [isRouting, setIsRouting] = useState(false);
  const [routeResult, setRouteResult] = useState<RouteNewLeadsResult | null>(null);

  // Phone number health state
  interface PhoneNumberRow {
    phone_number: string;
    state_code: string;
    purpose: string;
    is_active: boolean;
    consecutive_failures: number;
    failures_24h: number;
    last_failure_at: string | null;
    friendly_name: string | null;
    disabled_reason: string | null;
  }
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberRow[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [phonesLoaded, setPhonesLoaded] = useState(false);

  const fetchPhoneNumbers = useCallback(async () => {
    setLoadingPhones(true);
    const { data, error } = await supabase
      .from("twilio_phone_numbers")
      .select("phone_number, state_code, purpose, is_active, consecutive_failures, failures_24h, last_failure_at, friendly_name, disabled_reason")
      .order("is_active", { ascending: true })
      .order("consecutive_failures", { ascending: false });
    if (error) {
      console.error("[AdminSystem] Failed to fetch phone numbers:", error);
      toast.error("Failed to load phone numbers");
    } else {
      setPhoneNumbers((data ?? []) as PhoneNumberRow[]);
    }
    setLoadingPhones(false);
    setPhonesLoaded(true);
  }, []);

  const handleReEnableNumber = async (phoneNumber: string) => {
    const { error } = await supabase
      .from("twilio_phone_numbers")
      .update({ is_active: true, consecutive_failures: 0, failures_24h: 0, disabled_reason: null })
      .eq("phone_number", phoneNumber);
    if (error) {
      toast.error("Failed to re-enable number");
    } else {
      toast.success(`Re-enabled ${phoneNumber}`);
      fetchPhoneNumbers();
    }
  };

  const handleReEnableAll = async () => {
    // Only re-enable auto-disabled numbers — skip numbers disabled for other_platform or manual reasons
    const autoDisabled = phoneNumbers.filter((p) => !p.is_active && p.disabled_reason !== "other_platform" && p.disabled_reason !== "manual");
    if (autoDisabled.length === 0) {
      toast.info("No auto-disabled numbers to re-enable");
      return;
    }
    const phoneList = autoDisabled.map((p) => p.phone_number);
    const { error } = await supabase
      .from("twilio_phone_numbers")
      .update({ is_active: true, consecutive_failures: 0, failures_24h: 0, disabled_reason: null })
      .in("phone_number", phoneList);
    if (error) {
      toast.error("Failed to re-enable numbers");
    } else {
      toast.success(`Re-enabled ${autoDisabled.length} auto-disabled number(s)`);
      fetchPhoneNumbers();
    }
  };

  
  const handleRunSimulation = async (presetName: SimulationPresetName) => {
    setIsSimulating(true);
    setActivePreset(presetName);
    setSimulationResult(null);
    
    // Use setTimeout to allow UI to update before CPU-intensive work
    setTimeout(() => {
      try {
        const result = runPresetSimulation(presetName, { debug: false });
        setSimulationResult(result);
        
        // Log to console for detailed inspection
        console.log('[Simulation Result]', result);
        console.log(formatResultSummary(result));
        
        if (result.healthScore >= 80) {
          toast.success(`Simulation complete: Health Score ${result.healthScore}/100`);
        } else if (result.healthScore >= 50) {
          toast.warning(`Simulation complete: Health Score ${result.healthScore}/100 - Some issues detected`);
        } else {
          toast.error(`Simulation complete: Health Score ${result.healthScore}/100 - Critical issues found`);
        }
      } catch (error) {
        console.error('[Simulation Error]', error);
        toast.error('Simulation failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      } finally {
        setIsSimulating(false);
        setActivePreset(null);
      }
    }, 50);
  };

  const handleRouteNewLeads = async () => {
    setIsRouting(true);
    setRouteResult(null);
    try {
      const result = await routeNewLeads({ limit: 25 });
      setRouteResult(result);
      if (result.processed === 0) {
        toast.info("No new leads to process.");
      } else {
        toast.success(`Processed ${result.processed} lead(s).`);
      }
    } catch (error) {
      console.error("[Routing Error]", error);
      toast.error("Failed to process new leads.");
    } finally {
      setIsRouting(false);
    }
  };
  
  const getHealthColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 50) return "text-amber-600";
    return "text-destructive";
  };
  
  const getHealthBg = (score: number) => {
    if (score >= 80) return "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800";
    if (score >= 50) return "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800";
    return "bg-destructive/10 border-destructive/20";
  };

  // Calculate summary from result
  const getSummary = (result: SimulationResult) => {
    const successRate = result.systemMetrics.totalLeadsSimulated > 0
      ? ((result.systemMetrics.routed / result.systemMetrics.totalLeadsSimulated) * 100).toFixed(1)
      : 0;
    return `Processed ${result.systemMetrics.totalLeadsSimulated} leads with ${successRate}% success rate over ${result.simulatedTimeRange.durationDays} days`;
  };


  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <FlaskConical className="h-6 w-6" />
            System Operations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Run live routing actions and system diagnostics in one place.
          </p>
        </div>

        {/* Live Operations */}
        <Card className="border border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Live Operations
            </CardTitle>
            <CardDescription>
              Processes new leads in production (status NEW) and routes them to eligible agents.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-muted-foreground">
                Use this button whenever you want to trigger routing manually.
              </div>
              <Button onClick={handleRouteNewLeads} disabled={isRouting}>
                {isRouting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Process New Leads
              </Button>
            </div>

            {routeResult && (
              <div className="rounded-lg border bg-background p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Result: {routeResult.processed} lead(s) processed
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {routeResult.results.slice(0, 6).map((item) => (
                    <span key={item.leadId} className="px-2 py-1 rounded bg-muted">
                      {item.outcome}
                    </span>
                  ))}
                {routeResult.results.length > 6 && (
                  <span className="px-2 py-1 rounded bg-muted">
                    +{routeResult.results.length - 6} more
                  </span>
                )}
              </div>
              {routeResult.processed === 0 && (
                <div className="mt-2 text-xs text-muted-foreground">
                  No leads with status NEW were found. Create a test lead in Routing Lab or ingest via webhook.
                </div>
              )}
            </div>
          )}
          </CardContent>
        </Card>

        {/* DEV Agent Controls - Only visible in dev mode */}
        {isDevMode && (
          <Collapsible defaultOpen={false}>
            <Card>
              <CardHeader className="py-3">
                <CollapsibleTrigger className="flex w-full items-center justify-between text-left">
                  <div>
                    <CardTitle className="text-sm">DEV Agent Controls (Internal Only)</CardTitle>
                    <CardDescription>Session-scoped overrides for routing simulation</CardDescription>
                  </div>
                  <Badge variant="outline">Hidden by default</Badge>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <DevAgentControlsCard />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Integrations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PlugZap className="h-4 w-4" />
              Integrations
            </CardTitle>
            <CardDescription>
              Configure external systems used for intake, billing, and communications.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              {[
                { name: "LeadCapture", description: "Webhook intake for multi-survey lead sources." },
                { name: "Stripe", description: "Billing, contracts, and invoicing." },
                { name: "Twilio", description: "SMS and calling for agents." },
              ].map((item) => (
                <div key={item.name} className="rounded-lg border p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{item.name}</div>
                    <Badge variant="secondary">Not configured</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{item.description}</div>
                  <Button variant="outline" size="sm" disabled>
                    Configure
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Phone Number Health */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone Number Health (10DLC Pool)
            </CardTitle>
            <CardDescription>
              View Twilio number status. Numbers are auto-disabled after 3 consecutive failures.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchPhoneNumbers} disabled={loadingPhones}>
                {loadingPhones ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                {phonesLoaded ? "Refresh" : "Load Numbers"}
              </Button>
              {phonesLoaded && phoneNumbers.some((p) => !p.is_active && p.disabled_reason !== "other_platform" && p.disabled_reason !== "manual") && (
                <Button variant="default" size="sm" onClick={handleReEnableAll}>
                  Re-enable Auto-Disabled
                </Button>
              )}
            </div>

            {phonesLoaded && (
              <>
                <div className="flex gap-3 text-sm">
                  <span className="text-green-600 font-medium">
                    {phoneNumbers.filter((p) => p.is_active).length} active
                  </span>
                  <span className="text-red-600 font-medium">
                    {phoneNumbers.filter((p) => !p.is_active).length} disabled
                  </span>
                  <span className="text-muted-foreground">
                    {phoneNumbers.length} total
                  </span>
                </div>

                {phoneNumbers.some((p) => !p.is_active) && (
                  <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-medium text-red-700 dark:text-red-300">Disabled Numbers</span>
                    </div>
                    <div className="space-y-2">
                      {phoneNumbers.filter((p) => !p.is_active).map((p) => (
                        <div key={p.phone_number} className="flex items-center justify-between text-xs">
                          <div>
                            <span className="font-mono">{p.phone_number}</span>
                            <span className="text-muted-foreground ml-2">{p.state_code} · {p.purpose}</span>
                            {p.disabled_reason === "other_platform" ? (
                              <span className="text-amber-600 ml-2">other platform</span>
                            ) : p.disabled_reason === "manual" ? (
                              <span className="text-amber-600 ml-2">manually disabled</span>
                            ) : (
                              <span className="text-red-500 ml-2">{p.consecutive_failures} failures{p.failures_24h > 0 ? ` (${p.failures_24h} in 24h)` : ""}</span>
                            )}
                          </div>
                          <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => handleReEnableNumber(p.phone_number)}>
                            Re-enable
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {phoneNumbers.some((p) => p.is_active && p.consecutive_failures > 0) && (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Numbers with Failures (still active)</span>
                    </div>
                    <div className="space-y-1">
                      {phoneNumbers.filter((p) => p.is_active && p.consecutive_failures > 0).map((p) => (
                        <div key={p.phone_number} className="text-xs">
                          <span className="font-mono">{p.phone_number}</span>
                          <span className="text-muted-foreground ml-2">{p.state_code}</span>
                          <span className="text-amber-600 ml-2">{p.consecutive_failures}/3 failures</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {phonesLoaded && phoneNumbers.every((p) => p.is_active && p.consecutive_failures === 0) && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    All {phoneNumbers.length} numbers are healthy
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* System Health Diagnostics */}
        <SystemHealthDiagnostics />

        {/* Diagnostics — DEV only */}
        {isDevMode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Diagnostics (Simulations)
            </CardTitle>
            <CardDescription>
              These simulations are diagnostic only and do not create persistent data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Simulations do NOT create persistent data
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  For live lead testing, use Routing Lab. Simulations are for contract convergence, pacing, and fairness checks.
                </p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {ALL_PRESETS.map((preset) => (
                <div
                  key={preset.name}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div>
                    <p className="font-medium">{preset.displayName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {preset.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        {preset.estimatedDuration}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {preset.config.numberOfLeads} leads
                      </Badge>
                      {preset.config.numberOfLeads > 1000 && (
                        <Badge variant="destructive" className="text-xs">
                          ⚠ May freeze UI
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunSimulation(preset.name)}
                    disabled={isSimulating}
                  >
                    {isSimulating && activePreset === preset.name ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Run"
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Simulation Results — DEV only */}
        {isDevMode && simulationResult && (
          <Card className={`border-2 ${getHealthBg(simulationResult.healthScore)}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {simulationResult.healthScore >= 80 ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : simulationResult.healthScore >= 50 ? (
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-destructive" />
                  )}
                  <div>
                    <CardTitle className="text-lg">Simulation Complete</CardTitle>
                    <CardDescription>
                      {getSummary(simulationResult)}
                    </CardDescription>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-3xl font-bold ${getHealthColor(simulationResult.healthScore)}`}>
                    {simulationResult.healthScore}
                  </span>
                  <span className="text-sm text-muted-foreground">/100</span>
                  <p className="text-xs text-muted-foreground">Health Score</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Activity className="h-4 w-4" />
                    <span className="text-xs">Total Leads</span>
                  </div>
                  <span className="text-xl font-bold">{simulationResult.systemMetrics.totalLeadsSimulated}</span>
                </div>
                <div className="p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-xs">Routed</span>
                  </div>
                  <span className="text-xl font-bold text-green-600">
                    {simulationResult.systemMetrics.routed}
                  </span>
                </div>
                <div className="p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <RefreshCw className="h-4 w-4" />
                    <span className="text-xs">Retries</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">
                    {simulationResult.systemMetrics.retried}
                  </span>
                </div>
                <div className="p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Package className="h-4 w-4" />
                    <span className="text-xs">Stocked</span>
                  </div>
                  <span className="text-xl font-bold">
                    {simulationResult.systemMetrics.stocked}
                  </span>
                </div>
              </div>

              {/* Issues Detected */}
              {simulationResult.issues.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Issues Detected</p>
                  <div className="space-y-2">
                    {simulationResult.issues.map((issue, i) => (
                      <div 
                        key={i}
                        className={`p-3 rounded-lg border ${
                          issue.severity === 'critical' 
                            ? 'bg-destructive/10 border-destructive/20' 
                            : issue.severity === 'warning'
                            ? 'bg-amber-500/10 border-amber-500/20'
                            : 'bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {issue.severity === 'critical' ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : issue.severity === 'warning' ? (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          ) : (
                            <Activity className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="text-sm font-medium">{issue.category}</span>
                          <Badge 
                            variant={issue.severity === 'critical' ? 'destructive' : 'secondary'}
                            className="text-xs ml-auto"
                          >
                            {issue.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-6">
                          {issue.message}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Agent Distribution */}
              {simulationResult.agentMetrics && simulationResult.agentMetrics.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Agent Distribution
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {simulationResult.agentMetrics.slice(0, 8).map((agent) => (
                      <div key={agent.agentId} className="p-2 bg-background rounded border text-xs">
                        <p className="font-medium truncate">{agent.agentName}</p>
                        <p className="text-muted-foreground">
                          {agent.leadsReceived} leads
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* No Results Yet — DEV only */}
        {isDevMode && !simulationResult && !isSimulating && (
          <Card className="border-dashed">
            <CardContent className="py-12">
              <div className="text-center text-muted-foreground">
                <FlaskConical className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  Run a simulation preset to validate system behavior
                </p>
                <p className="text-xs mt-1">
                  Results will show health score, contract breaches, and pool starvation metrics
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
